import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router'

import { useNotifications } from 'app/application/application'
import { ConnectionSetupRoutes } from 'app/application/types'
import { useConnectionsApi } from 'app/connections/application'
import { SupportedAccount } from 'app/connections/types'

import { ActivityIndicator, Button } from 'components'

import { useConnection } from '../../components/ConnectionContext'
import { mapToConnectionName } from '../../connectionUrlMapper'
import connectStyles from '../Connect.module.scss'
import styles from './Steps.module.scss'
import { Accounts } from './components/Accounts/Accounts'
import { ConnectionStepper } from './components/ConnectionStepper/ConnectionStepper'

type SelectAccountsParams = {
  name: string
}

const singleSelectionConnections = ['googleAnalytics']

const SelectAccounts = () => {
  const navigate = useNavigate()
  const { createNotification, te } = useNotifications()
  const { name } = useParams<SelectAccountsParams>() as SelectAccountsParams
  const { t } = useTranslation('connections')

  const [availableAccounts, setAvailableAccounts] = useState<SupportedAccount[]>([])
  const [selectedAccounts, setSelectedAccounts] = useState<SupportedAccount[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const connectionName = mapToConnectionName(name)

  const connection = useConnection()
  const { getAccounts } = useConnectionsApi(connectionName)

  const isSingleSelection = singleSelectionConnections.includes(connectionName)

  useEffect(
    () =>
      void getAccounts()
        .then((result) => {
          if (result.ok) {
            setAvailableAccounts(result.val)
          } else {
            createNotification({
              message: result.val.message,
              title: te(`http.${result.val.code}`),
              type: 'error',
            })
          }
        })
        .finally(() => setIsLoading(false)),
    [createNotification, getAccounts, name, te],
  )

  if (!connection || connection.name !== connectionName) {
    return <Navigate to={`/connections/${name}/setup`} replace />
  }

  const handleAccountClick = (account: SupportedAccount) =>
    setSelectedAccounts((prev) =>
      isSingleSelection
        ? prev.includes(account)
          ? []
          : [account]
        : prev.includes(account)
          ? prev.filter((a) => a.id !== account.id)
          : [...prev, account],
    )

  return (
    <>
      <ConnectionStepper activeStep={2} />
      <section className={connectStyles.accountSelectionCard}>
        <header>
          <h2>{t(`setup.${connection.name}.selectAccounts.title`)}</h2>
          <p>{t(`setup.${connection.name}.selectAccounts.description`)}</p>
        </header>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <ActivityIndicator size="l" />
          </div>
        ) : (
          <Accounts
            accounts={availableAccounts}
            selectionOptions={{
              isSelected: (account) => selectedAccounts.includes(account),
              onClick: handleAccountClick,
            }}
            connectionName={connection.name}
            variant="selection"
          />
        )}
      </section>

      <div className={connectStyles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>

        <Button
          disabled={!selectedAccounts.length}
          onClick={(e) => {
            e.stopPropagation()
            void navigate(`../${ConnectionSetupRoutes.ConnectionVerified}`, {
              state: { accounts: selectedAccounts, connection: connection },
            })
          }}
        >
          {t('setup.signIn.continue')}
        </Button>
      </div>
    </>
  )
}

export default SelectAccounts
